import { PageIndices } from '../../common/i-doc/i-doc.registry.ts';
import React, { useCallback, useEffect, useState } from 'react';
import {
  Box,
  Button,
  CircularProgress,
  Alert,
  Menu,
  MenuItem,
  ListItemIcon,
  ListItemText,
  Backdrop,
} from '@mui/material';

import FastfoodIcon from '@mui/icons-material/Fastfood';
import FitnessCenterIcon from '@mui/icons-material/FitnessCenter';
import LocalDrinkIcon from '@mui/icons-material/LocalDrink';
import MoodIcon from '@mui/icons-material/Mood';
import MedicalServicesIcon from '@mui/icons-material/MedicalServices';
import AssignmentIcon from '@mui/icons-material/Assignment';
import EventNoteIcon from '@mui/icons-material/EventNote';
import AutoAwesomeIcon from '@mui/icons-material/AutoAwesome';
import { useSelector } from 'react-redux';
import { selectUser } from '@store/auth/authSlice';
import {
  getCalendarEventById,
  getCalendarEvents,
  getClientCalendarOptions,
  deleteCalendarEvent,
  updateCalendarEvent,
  copyCalendarEvent,
  createClientFeelingEvent,
  createClientTaskEvent,
} from '@services/calendar.service';
import { Calendar as AppCalendar, CalendarEventDto, CalendarEventType } from '@/types/calendar';
import { PageHint } from '../common/PageHint';
import { CalendarWidget, CalendarFetchParams } from '@components/calendar/CalendarWidget';
import { CalendarEventDetailModal } from '@components/clients/calendar/CalendarEventDetailModal';
import { AddClientEventModal, IntakeEventCode } from '@components/clients/calendar/AddClientEventModal';
import { AddFoodEventModal } from '@components/calendar/add-event-modals/AddFoodEventModal';
import { AddWorkoutEventModal } from '@components/calendar/add-event-modals/AddWorkoutEventModal';
import { AddMedicationEventModal } from '@components/calendar/add-event-modals/AddMedicationEventModal';
import { saveFoodEvent } from '@services/food.service';
import { saveWorkoutIntake, SaveIntakeOptions } from '@services/workout.service';
import { saveMedicationIntake } from '@services/medication.service';
import { AddFeelingEventModalV2 } from '@components/calendar/add-event-modals/AddFeelingEventModalV2';
import { AddTaskEventModalV2 } from '@components/calendar/add-event-modals/AddTaskEventModalV2';
import { QuickFoodAddModalV2 } from '@components/dashboard/QuickFoodAddModalV2';
import { QuickWorkoutAddModalV2 } from '@components/dashboard/QuickWorkoutAddModalV2';
import { QuickMedicationAddModalV2 } from '@components/dashboard/QuickMedicationAddModalV2';
import { QuickWaterAddModal } from '@components/dashboard/QuickWaterAddModal';
import { FoodEventDetailModal } from '@components/dashboard/FoodEventDetailModal';
import { ConfirmationDialog } from '@components/common/ConfirmationDialog';
import { toastService } from '@services/toast.service.ts';
import { format } from 'date-fns';
import { toLocalIso } from '@/utils/datetime';
import { getClientSleepSessions, sleepFactsInRange } from '@/services/sleep.service';
import { getDayTaskCompletion, getEventCompletionStatus } from '@components/clients/calendar/day-task-completion/dayTaskCompletion';
import { EventCardStatus } from '@components/clients/calendar/CalendarEventStatusCard';
import { DayTaskCompletionBadge } from '@components/clients/calendar/day-task-completion/DayTaskCompletionBadge';
import { useNowInTimezone } from '@components/clients/calendar/day-task-completion/useNowInTimezone';
import { toDayBalanceView } from '@components/clients/calendar/day-balance/dayBalance';
import { DayNutrientBoxes } from '@components/clients/calendar/day-balance/DayNutrientBoxes';
import { DayRange, useClientDayBalance } from '@components/clients/calendar/day-balance/useClientDayBalance';
import { getDayNormProgress } from '@components/clients/calendar/day-norm-progress/dayNormProgress';
import { useClientNorms } from '@components/clients/daily-norms/useClientNorms';
import { useClientNormsByDay } from '@components/clients/daily-norms/useClientNormsByDay';

const getEventTypeIcon = (code: string) => {
  switch (code) {
    case 'food':       return <FastfoodIcon fontSize="small" />;
    case 'workout':    return <FitnessCenterIcon fontSize="small" />;
    case 'liquid':     return <LocalDrinkIcon fontSize="small" />;
    case 'feeling':    return <MoodIcon fontSize="small" />;
    case 'medication': return <MedicalServicesIcon fontSize="small" />;
    case 'task':       return <AssignmentIcon fontSize="small" />;
    default:           return <EventNoteIcon fontSize="small" />;
  }
};

interface ClientCalendarTabProps {
  clientId: string;
  /** Часовий пояс клієнта (IANA) - для підрахунку виконання призначень за його місцевим часом. */
  clientTimezone?: string | null;
}

export const ClientCalendarTab: React.FC<ClientCalendarTabProps> = ({ clientId, clientTimezone }) => {
  const clientNowIso = useNowInTimezone(clientTimezone);
  const currentUser = useSelector(selectUser);

  const [clientCalendars, setClientCalendars] = useState<AppCalendar[]>([]);
  const [eventTypes, setEventTypes] = useState<CalendarEventType[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [refreshTrigger, setRefreshTrigger] = useState(0);
  const [currentDateForModal, setCurrentDateForModal] = useState(new Date());


  // Modal: add/edit generic event
  const [addEventOpen, setAddEventOpen] = useState(false);
  const [isQuickFoodModalOpen, setIsQuickFoodModalOpen] = useState(false);
  const [isQuickWorkoutModalOpen, setIsQuickWorkoutModalOpen] = useState(false);
  const [isQuickMedicationModalOpen, setIsQuickMedicationModalOpen] = useState(false);
  const [isQuickWaterModalOpen, setIsQuickWaterModalOpen] = useState(false);
  const [isQuickFeelingModalOpen, setIsQuickFeelingModalOpen] = useState(false);
  const [isQuickTaskModalOpen, setIsQuickTaskModalOpen] = useState(false);
  // AI-подія (details.components) редагується тією ж AI-модалкою, що й створювалась.
  const [aiEventToEdit, setAiEventToEdit] = useState<CalendarEventDto | null>(null);
  const [eventToEdit, setEventToEdit] = useState<CalendarEventDto | null>(null);
  const [defaultEventTypeId, setDefaultEventTypeId] = useState<number | null>(null);

  // Modal: add/edit food / workout / medication (словник + нутрієнти/калорії)
  const [intakeModal, setIntakeModal] = useState<IntakeEventCode | null>(null);
  const [intakeEventToEdit, setIntakeEventToEdit] = useState<CalendarEventDto | null>(null);
  const openIntakeModal = useCallback((code: IntakeEventCode, event: CalendarEventDto | null = null) => {
    setIntakeEventToEdit(event);
    setIntakeModal(code);
  }, []);
  const closeIntakeModal = () => {
    setIntakeModal(null);
    setIntakeEventToEdit(null);
    setCellClickDate(null);
  };

  // Modal: event detail
  const [detailEventId, setDetailEventId] = useState<number | null>(null);
  const [detailEvent, setDetailEvent] = useState<CalendarEventDto | null>(null);
  const [detailLoading, setDetailLoading] = useState(false);

  // Modal: food event detail
  const [isFoodDetailModalOpen, setIsFoodDetailModalOpen] = useState(false);
  const [foodDetailEvent, setFoodDetailEvent] = useState<CalendarEventDto | null>(null);

  // Modal: delete confirmation
  const [isConfirmDeleteDialogOpen, setIsConfirmDeleteDialogOpen] = useState(false);
  const [eventToDelete, setEventToDelete] = useState<CalendarEventDto | null>(null);

  // Add event menus
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const addMenuOpen = Boolean(anchorEl);

  const [quickAddAnchorEl, setQuickAddAnchorEl] = useState<null | HTMLElement>(null);
  const quickAddMenuOpen = Boolean(quickAddAnchorEl);

  // Cell click: pre-filled date for new events
  const [cellClickDate, setCellClickDate] = useState<Date | null>(null);

  const handleCellQuickAddEvent = useCallback((date: Date, anchor: HTMLElement) => {
    setCellClickDate(date);
    setQuickAddAnchorEl(anchor);
  }, []);

  const handleEventMove = useCallback(async (eventId: number, newDate: Date, copy: boolean) => {
    try {
      if (copy) {
        await copyCalendarEvent(eventId, { scheduledLdt: toLocalIso(newDate) });
        toastService.success('Подію скопійовано');
      } else {
        await updateCalendarEvent(eventId, { scheduledLdt: toLocalIso(newDate) });
        toastService.success('Подію перенесено');
      }
      setRefreshTrigger(prev => prev + 1);
    } catch {
      toastService.error(copy ? 'Не вдалося скопіювати подію' : 'Не вдалося перенести подію');
    }
  }, []);

  const handleAddEventWithDate = useCallback((date: Date, eventTypeCode: string) => {
    setCellClickDate(date);
    if (eventTypeCode === 'food' || eventTypeCode === 'workout' || eventTypeCode === 'medication') {
      openIntakeModal(eventTypeCode);
    } else {
      const et = eventTypes.find(t => t.code === eventTypeCode);
      setDefaultEventTypeId(et?.id ?? null);
      setEventToEdit(null);
      setAddEventOpen(true);
    }
  }, [eventTypes, openIntakeModal]);

  useEffect(() => {
    const fetchOptions = async () => {
      try {
        const options = await getClientCalendarOptions(clientId);
        setClientCalendars(options.calendars);
        setEventTypes(options.eventTypes);
      } catch {
        setError('Не вдалося завантажити параметри календаря');
      } finally {
        setLoading(false);
      }
    };
    fetchOptions();
  }, [clientId]);

  const fetchEvents = useCallback(
    async ({ calendarIds, eventTypeIds, dateFrom, dateTo }: CalendarFetchParams) => {
      return getCalendarEvents({
        clientId,
        calendarId:
          calendarIds.length === clientCalendars.length ? undefined : calendarIds.join(','),
        eventTypeIds: eventTypeIds.join(','),
        dateFrom,
        dateTo,
        limit: 500,
      });
    },
    [clientId, clientCalendars.length],
  );

  // Відсоток виконаних подій, які створив поточний експерт, у заголовку дня - за місцевим часом клієнта.
  // Блок завжди видно, навіть без жодного призначення на день (власник: так однаково видно, що день
  // "порожній", а не що щось не завантажилось) - DayTaskCompletionBadge сам показує «Виконано –»
  // і порожню смугу, коли completion ще null.
  const expertId = currentUser?.id;
  const renderDayTaskCompletion = useCallback(
    (day: Date, events: CalendarEventDto[], compact: boolean) => {
      if (!expertId) return null;
      const dayKey = format(day, 'yyyy-MM-dd');
      const completion = getDayTaskCompletion(events, { day: dayKey, creatorId: expertId, clientNowIso });
      return <DayTaskCompletionBadge completion={completion} compact={compact} />;
    },
    [expertId, clientNowIso],
  );

  // Смуга «Виконано» / «Невиконано» - лише на подіях поточного експерта, за тими ж правилами, що й відсоток.
  const getEventStatus = useCallback(
    (event: CalendarEventDto): EventCardStatus =>
      (expertId ? getEventCompletionStatus(event, { creatorId: expertId, clientNowIso }) : null) ?? 'none',
    [expertId, clientNowIso],
  );

  // Нутрієнти й витрата енергії за день у заголовку дня - з усіх подій дня (і непідтверджених), рахує бекенд.
  const [visibleRange, setVisibleRange] = useState<DayRange | null>(null);
  const dayBalances = useClientDayBalance(clientId, visibleRange, refreshTrigger);
  // Під ними - виконання денних норм у відсотках: кожен день - від норм, що діяли того дня (версія власних норм
  // експерта ?? стандартна; стандартні - поточні, історії не мають).
  const { standard: standardNorms } = useClientNorms(clientId);
  const normsOnDay = useClientNormsByDay(clientId, visibleRange, standardNorms, refreshTrigger);
  const renderDayBalance = useCallback(
    (day: Date, compact: boolean) => {
      const date = format(day, 'yyyy-MM-dd');
      const balance = toDayBalanceView(dayBalances[date]);
      if (!balance) return null;
      const { norms, own } = normsOnDay(date);
      const progress = getDayNormProgress(balance, norms);
      return <DayNutrientBoxes balance={balance} progress={progress} dailyNorms={norms} own={own} compact={compact} />;
    },
    [dayBalances, normsOnDay],
  );

  // Сон клієнта ("Ліг" / "Встав") - лише перегляд; без дозволу клієнта "Календар: Сон" бекенд дає 403 -> просто без сну.
  const fetchSleepFacts = useCallback(
    async (from: Date, to: Date) => sleepFactsInRange(await getClientSleepSessions(clientId, from, to), from, to),
    [clientId],
  );

  const openEventDetail = async (eventId: number) => {
    setDetailLoading(true);
    setDetailEvent(null);
    try {
      const event = await getCalendarEventById(eventId);
      if (event.eventType.code === 'food' && event.isStructured !== false) {
        setFoodDetailEvent(event);
        setIsFoodDetailModalOpen(true);
      } else {
        setDetailEvent(event);
        setDetailEventId(eventId);
      }
    } catch {
      toastService.error('Не вдалося завантажити деталі події');
    } finally {
      setDetailLoading(false);
    }
  };

  const handleEditEvent = (event: CalendarEventDto) => {
    const code = event.eventType.code;
    if (event.source === 'ai' && (code === 'food' || code === 'workout' || code === 'medication')) {
      setAiEventToEdit(event);
      if (code === 'food') setIsQuickFoodModalOpen(true);
      else if (code === 'workout') setIsQuickWorkoutModalOpen(true);
      else setIsQuickMedicationModalOpen(true);
      return;
    }
    if (code === 'food' || code === 'workout' || code === 'medication') {
      openIntakeModal(code, event);
    } else {
      setEventToEdit(event);
      setAddEventOpen(true);
    }
  };

  const handleDeleteRequest = (event: CalendarEventDto) => {
    setEventToDelete(event);
    setIsConfirmDeleteDialogOpen(true);
  };

  const handleConfirmDelete = async () => {
    if (!eventToDelete) return;
    try {
      await deleteCalendarEvent(eventToDelete.id);
      toastService.success('Подію успішно видалено');
      setRefreshTrigger(prev => prev + 1);
    } catch {
      toastService.error('Не вдалося видалити подію');
    } finally {
      setIsConfirmDeleteDialogOpen(false);
      setEventToDelete(null);
    }
  };

  // Події експерта без calendarId бекенд кладе в календар експерта (створює за потреби) - оновлюємо список календарів.
  const handleEventCreated = () => {
    getClientCalendarOptions(clientId)
      .then(options => {
        setClientCalendars(options.calendars);
        setEventTypes(options.eventTypes);
      })
      .catch(() => {});
    setRefreshTrigger(prev => prev + 1);
  };

  // Створення - через /client/:clientId/...; редагування - спільні ендпоінти події.
  const saveIntakeEvent = async (
    save: (data: any, options: SaveIntakeOptions) => Promise<unknown>,
    data: any,
    messages: { created: string; updated: string },
  ) => {
    try {
      if (intakeEventToEdit) {
        await save(data, { eventId: intakeEventToEdit.id });
        toastService.success(messages.updated);
        setRefreshTrigger(prev => prev + 1);
      } else {
        await save(data, { clientId });
        toastService.success(messages.created);
        handleEventCreated();
      }
      closeIntakeModal();
    } catch (err: any) {
      toastService.error(err?.response?.data?.message || err?.message || 'Не вдалося зберегти подію');
    }
  };

  const handleSaveFoodEvent = (data: any) =>
    saveIntakeEvent(saveFoodEvent, data, { created: 'Прийом їжі успішно додано', updated: 'Прийом їжі успішно оновлено' });

  const handleSaveWorkoutEvent = (data: any) =>
    saveIntakeEvent(saveWorkoutIntake, data, { created: 'Тренування успішно додано', updated: 'Тренування успішно оновлено' });

  const handleSaveMedicationEvent = (data: any) =>
    saveIntakeEvent(saveMedicationIntake, data, { created: 'Препарат успішно додано', updated: 'Препарат успішно оновлено' });

  const handleSaveFeelingEvent = async (data: { title: string; scheduledLdt?: string; isCompleted: boolean }) => {
    try {
      const now = new Date();
      const scheduledLdtFinal = data.scheduledLdt || toLocalIso(new Date(currentDateForModal.getFullYear(), currentDateForModal.getMonth(), currentDateForModal.getDate(), now.getHours(), now.getMinutes(), now.getSeconds()));

      await createClientFeelingEvent(clientId, {
        title: data.title,
        scheduledLdt: scheduledLdtFinal,
        isCompleted: data.isCompleted,
      });

      toastService.success('Думки успішно додано');
      setIsQuickFeelingModalOpen(false);
      handleEventCreated();
    } catch (err: any) {
      toastService.error(err?.message || 'Не вдалося зберегти думки');
    }
  };

  const handleSaveTaskEvent = async (data: any) => {
    try {
      await createClientTaskEvent(clientId, data);

      toastService.success('Задачу успішно додано');
      setIsQuickTaskModalOpen(false);
      handleEventCreated();
    } catch (err: any) {
      toastService.error(err?.message || 'Не вдалося зберегти задачу');
    }
  };

  const addEventButton = (
    <Box sx={{ display: 'flex', gap: 0.5, alignItems: 'center' }}>
      <Button
        variant="outlined"
        size="small"
        onClick={e => setQuickAddAnchorEl(e.currentTarget)}
        sx={{ minWidth: 0, px: 1, py: 0, height: 26, color: '#ff8f00', borderColor: '#ff8f00' }}
        title="Швидке додавання (AI)"
      >
        <AutoAwesomeIcon sx={{ fontSize: '1rem' }} />
      </Button>
      <Menu
        anchorEl={quickAddAnchorEl}
        open={quickAddMenuOpen}
        onClose={() => setQuickAddAnchorEl(null)}
        MenuListProps={{ 'aria-labelledby': 'quick-add-button' }}
      >
        <MenuItem
          onClick={() => {
            setQuickAddAnchorEl(null);
            setIsQuickFoodModalOpen(true);
          }}
        >
          <ListItemIcon><FastfoodIcon fontSize="small" sx={{ color: '#ff8f00' }} /></ListItemIcon>
          <ListItemText>Додати їжу (AI)</ListItemText>
        </MenuItem>
        <MenuItem
          onClick={() => {
            setQuickAddAnchorEl(null);
            setIsQuickWorkoutModalOpen(true);
          }}
        >
          <ListItemIcon><FitnessCenterIcon fontSize="small" sx={{ color: '#d32f2f' }} /></ListItemIcon>
          <ListItemText>Додати тренування (AI)</ListItemText>
        </MenuItem>
        <MenuItem
          onClick={() => {
            setQuickAddAnchorEl(null);
            setIsQuickMedicationModalOpen(true);
          }}
        >
          <ListItemIcon><MedicalServicesIcon fontSize="small" sx={{ color: '#388e3c' }} /></ListItemIcon>
          <ListItemText>Додати препарат (AI)</ListItemText>
        </MenuItem>
        <MenuItem
          onClick={() => {
            setQuickAddAnchorEl(null);
            setIsQuickWaterModalOpen(true);
          }}
        >
          <ListItemIcon><LocalDrinkIcon fontSize="small" sx={{ color: '#0288d1' }} /></ListItemIcon>
          <ListItemText>Додати воду</ListItemText>
        </MenuItem>
        <MenuItem
          onClick={() => {
            setQuickAddAnchorEl(null);
            setIsQuickFeelingModalOpen(true);
          }}
        >
          <ListItemIcon><MoodIcon fontSize="small" sx={{ color: '#7b1fa2' }} /></ListItemIcon>
          <ListItemText>Додати думки</ListItemText>
        </MenuItem>
        <MenuItem
          onClick={() => {
            setQuickAddAnchorEl(null);
            setIsQuickTaskModalOpen(true);
          }}
        >
          <ListItemIcon><AssignmentIcon fontSize="small" sx={{ color: '#00796b' }} /></ListItemIcon>
          <ListItemText>Додати задачу</ListItemText>
        </MenuItem>
      </Menu>

      <Button
        variant="contained"
        size="small"
        onClick={e => setAnchorEl(e.currentTarget)}
        disabled={eventTypes.length === 0}
        sx={{ minWidth: 0, px: 1, py: 0, height: 26, fontSize: '1rem', lineHeight: 1 }}
      >
        +
      </Button>
      <Menu
        anchorEl={anchorEl}
        open={addMenuOpen}
        onClose={() => setAnchorEl(null)}
        MenuListProps={{ 'aria-labelledby': 'add-event-button' }}
      >
        {eventTypes.map(eventType => (
          <MenuItem
            key={eventType.id}
            onClick={() => {
              setAnchorEl(null);
              const code = eventType.code;
              if (code === 'food' || code === 'workout' || code === 'medication') {
                openIntakeModal(code);
              } else {
                setDefaultEventTypeId(eventType.id);
                setEventToEdit(null);
                setAddEventOpen(true);
              }
            }}
          >
            <ListItemIcon>{getEventTypeIcon(eventType.code)}</ListItemIcon>
            <ListItemText>{eventType.name}</ListItemText>
          </MenuItem>
        ))}
      </Menu>
    </Box>
  );

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', height: '100%' }} data-i-doc={PageIndices.EXPERT_CLIENT_CALENDAR}>
      <PageHint iDoc={PageIndices.EXPERT_CLIENT_CALENDAR} />

      <Backdrop
        sx={{ color: '#fff', zIndex: theme => theme.zIndex.drawer + 1 }}
        open={detailLoading}
      >
        <CircularProgress color="inherit" />
      </Backdrop>

      {loading ? (
        <Box display="flex" justifyContent="center" sx={{ mt: 4 }}>
          <CircularProgress />
        </Box>
      ) : error ? (
        <Alert severity="error" sx={{ mt: 4 }}>{error}</Alert>
      ) : (
        <CalendarWidget
          calendars={clientCalendars}
          eventTypes={eventTypes}
          fetchEvents={fetchEvents}
          fetchSleepFacts={fetchSleepFacts}
          renderDayHeaderExtra={renderDayTaskCompletion}
          getEventStatus={getEventStatus}
          renderDayHeaderSummary={renderDayBalance}
          onVisibleRangeChange={setVisibleRange}
          viewerContext={currentUser ? { userId: currentUser.id, panelMode: 'expert' } : undefined}
          storagePrefix={`client-calendar-${clientId}`}
          extraActions={addEventButton}
          refreshTrigger={refreshTrigger}
          onEventClick={openEventDetail}
          onEditEvent={handleEditEvent}
          onDeleteEvent={handleDeleteRequest}
          onCurrentDateChange={setCurrentDateForModal}
          onAddEventWithDate={handleAddEventWithDate}
          onCellQuickAddEvent={handleCellQuickAddEvent}
          onEventMove={handleEventMove}
        />
      )}

      <CalendarEventDetailModal
        open={detailEventId !== null}
        event={detailEvent}
        loading={false}
        onClose={() => { setDetailEventId(null); setDetailEvent(null); }}
      />

      <FoodEventDetailModal
        open={isFoodDetailModalOpen}
        event={foodDetailEvent}
        onClose={() => { setIsFoodDetailModalOpen(false); setFoodDetailEvent(null); }}
        onEdit={event => {
          setIsFoodDetailModalOpen(false);
          setFoodDetailEvent(null);
          handleEditEvent(event);
        }}
      />

      <AddClientEventModal
        open={addEventOpen}
        onClose={() => { setAddEventOpen(false); setEventToEdit(null); setDefaultEventTypeId(null); setCellClickDate(null); }}
        clientId={clientId}
        eventTypes={eventTypes}
        defaultDate={cellClickDate ?? currentDateForModal}
        eventToEdit={eventToEdit}
        onSuccess={handleEventCreated}
        onIntakeTypeSelected={code => {
          setAddEventOpen(false);
          setEventToEdit(null);
          setDefaultEventTypeId(null);
          openIntakeModal(code);
        }}
        defaultEventTypeId={defaultEventTypeId}
      />

      <AddFoodEventModal
        open={intakeModal === 'food'}
        onClose={closeIntakeModal}
        onSave={handleSaveFoodEvent}
        eventToEdit={intakeModal === 'food' ? intakeEventToEdit : null}
        initialScheduledAt={cellClickDate ?? undefined}
      />
      <AddWorkoutEventModal
        open={intakeModal === 'workout'}
        onClose={closeIntakeModal}
        onSave={handleSaveWorkoutEvent}
        eventToEdit={intakeModal === 'workout' ? intakeEventToEdit : null}
        initialScheduledAt={cellClickDate ?? undefined}
      />
      <AddMedicationEventModal
        open={intakeModal === 'medication'}
        onClose={closeIntakeModal}
        onSave={handleSaveMedicationEvent}
        eventToEdit={intakeModal === 'medication' ? intakeEventToEdit : null}
        initialScheduledAt={cellClickDate ?? undefined}
      />

      <AddFeelingEventModalV2
        open={isQuickFeelingModalOpen}
        onClose={() => setIsQuickFeelingModalOpen(false)}
        onSave={handleSaveFeelingEvent}
      />

      <AddTaskEventModalV2
        open={isQuickTaskModalOpen}
        onClose={() => setIsQuickTaskModalOpen(false)}
        onSave={handleSaveTaskEvent}
        initialScheduledAt={currentDateForModal}
      />

      <QuickFoodAddModalV2
        open={isQuickFoodModalOpen}
        onClose={() => { setIsQuickFoodModalOpen(false); setAiEventToEdit(null); setCellClickDate(null); }}
        eventToEdit={aiEventToEdit}
        selectedDate={(cellClickDate ?? currentDateForModal).toISOString().split('T')[0]}
        initialScheduledAt={cellClickDate ?? undefined}
        onRefreshDashboard={() => setRefreshTrigger(prev => prev + 1)}
        clientId={clientId}
      />

      <QuickWorkoutAddModalV2
        open={isQuickWorkoutModalOpen}
        onClose={() => { setIsQuickWorkoutModalOpen(false); setAiEventToEdit(null); setCellClickDate(null); }}
        eventToEdit={aiEventToEdit}
        selectedDate={(cellClickDate ?? currentDateForModal).toISOString().split('T')[0]}
        initialScheduledAt={cellClickDate ?? undefined}
        onRefreshDashboard={() => setRefreshTrigger(prev => prev + 1)}
        clientId={clientId}
      />

      <QuickMedicationAddModalV2
        open={isQuickMedicationModalOpen}
        onClose={() => { setIsQuickMedicationModalOpen(false); setAiEventToEdit(null); setCellClickDate(null); }}
        eventToEdit={aiEventToEdit}
        selectedDate={(cellClickDate ?? currentDateForModal).toISOString().split('T')[0]}
        initialScheduledAt={cellClickDate ?? undefined}
        onRefreshDashboard={() => setRefreshTrigger(prev => prev + 1)}
        clientId={clientId}
      />

      <QuickWaterAddModal
        open={isQuickWaterModalOpen}
        onClose={() => { setIsQuickWaterModalOpen(false); setCellClickDate(null); }}
        selectedDate={(cellClickDate ?? currentDateForModal).toISOString().split('T')[0]}
        initialScheduledAt={cellClickDate ?? undefined}
        onRefreshDashboard={() => setRefreshTrigger(prev => prev + 1)}
        clientId={clientId}
      />

      <ConfirmationDialog
        open={isConfirmDeleteDialogOpen}
        onClose={() => { setIsConfirmDeleteDialogOpen(false); setEventToDelete(null); }}
        onConfirm={handleConfirmDelete}
        title="Підтвердити видалення"
        message={`Ви впевнені, що хочете видалити подію "${eventToDelete?.title}"? Цю дію неможливо буде скасувати.`}
        confirmText="Видалити"
        cancelText="Скасувати"
      />
    </Box>
  );
};
